import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Check, ChevronDown } from 'lucide-react';
import type { PersonCluster, PersonRole, PersonUpdatePayload } from '@/api/types/face.types';

interface PersonNamingFormProps {
  clusters: PersonCluster[];
  onSave: (id: string, payload: PersonUpdatePayload) => void;
  isSaving?: boolean;
}

const ROLE_OPTIONS: PersonRole[] = [
  'bride',
  'groom',
  'family',
  'guest',
  'vendor',
  'unknown',
];

function PersonRow({
  cluster,
  onSave,
  isSaving,
}: {
  cluster: PersonCluster;
  onSave: (id: string, payload: PersonUpdatePayload) => void;
  isSaving?: boolean;
}) {
  const { t } = useTranslation('faces');
  const [name, setName] = useState(cluster.name || '');
  const [role, setRole] = useState<PersonRole>(cluster.role || 'unknown');
  const [isDirty, setIsDirty] = useState(false);

  const handleNameChange = (value: string) => {
    setName(value);
    setIsDirty(value !== cluster.name || role !== cluster.role);
  };

  const handleRoleChange = (value: PersonRole) => {
    setRole(value);
    setIsDirty(name !== cluster.name || value !== cluster.role);
  };

  const handleSave = () => {
    onSave(cluster.id, { name: name.trim(), role });
    setIsDirty(false);
  };

  return (
    <div className="flex items-center gap-4 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
      {/* Face thumbnail */}
      {cluster.representative_face_url ? (
        <img
          src={cluster.representative_face_url}
          alt={name || t('unknownPerson')}
          className="h-14 w-14 flex-shrink-0 rounded-full object-cover ring-2 ring-white dark:ring-gray-800"
        />
      ) : (
        <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
          <User className="h-7 w-7 text-gray-400" />
        </div>
      )}

      {/* Info */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {t('faceCountShort', { count: cluster.face_count })} &middot;{' '}
            {t('photoCountShort', { count: cluster.photo_count })}
          </span>
        </div>

        {/* Sample faces row */}
        <div className="mt-1 flex gap-1">
          {cluster.sample_faces.slice(0, 4).map((face) =>
            face.crop_url ? (
              <img
                key={face.id}
                src={face.crop_url}
                alt="Face"
                className="h-7 w-7 rounded object-cover"
                loading="lazy"
              />
            ) : null,
          )}
        </div>
      </div>

      {/* Name input */}
      <input
        type="text"
        value={name}
        onChange={(e) => handleNameChange(e.target.value)}
        placeholder={t('namePlaceholder')}
        className="input-field w-40 text-sm"
      />

      {/* Role dropdown */}
      <div className="relative">
        <select
          value={role}
          onChange={(e) => handleRoleChange(e.target.value as PersonRole)}
          className="input-field w-28 appearance-none pe-8 text-sm"
        >
          {ROLE_OPTIONS.map((r) => (
            <option key={r} value={r}>
              {t(`roles.${r}`)}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute end-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
      </div>

      {/* Save button */}
      <button
        onClick={handleSave}
        disabled={!isDirty || isSaving}
        className={`flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
          isDirty
            ? 'bg-primary-600 text-white hover:bg-primary-700'
            : 'bg-gray-100 text-gray-400 dark:bg-gray-800'
        } disabled:opacity-50`}
      >
        <Check className="h-4 w-4" />
      </button>
    </div>
  );
}

export function PersonNamingForm({
  clusters,
  onSave,
  isSaving,
}: PersonNamingFormProps) {
  const { t } = useTranslation('faces');

  if (clusters.length === 0) {
    return (
      <p className="text-center text-sm text-gray-500 dark:text-gray-400">
        {t('noClusters')}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        {t('namingDescription')}
      </p>
      {clusters.map((cluster) => (
        <PersonRow
          key={cluster.id}
          cluster={cluster}
          onSave={onSave}
          isSaving={isSaving}
        />
      ))}
    </div>
  );
}
